import React, { useMemo } from 'react';

/**
 * O checklist do atendimento em leitura, como o associado o vê (entrada + saída).
 *
 * Extraído de `ChecklistPublicoPage` na at-126, para a página de acompanhamento mostrar o MESMO
 * bloco — duas cópias divergiriam no primeiro ajuste, e as duas telas são a mesma identidade para
 * quem recebe o link. Sem CSS próprio: usa as classes `cp-*` de `ChecklistPublicoPage.css`, que o
 * hospedeiro importa.
 */

const STATUS_LABELS: Record<string, string> = {
  OK: 'OK',
  DAMAGED: 'Avariado',
  MISSING: 'Ausente',
  NA: 'N/A',
};

type MediaGroup = { title: string; items: any[] };

/** Agrupa as mídias de uma sessão por seção (ângulo, seção personalizada ou imagens de saída). */
const groupSessionMedia = (session: any): MediaGroup[] => {
  if (!session?.media?.length) return [];

  const slotsById = new Map<string, any>((session.slots || []).map((s: any) => [s.id, s]));
  const groups = new Map<string, MediaGroup>();

  for (const media of session.media) {
    const slot = media.slotId ? slotsById.get(media.slotId) : null;
    const title = slot
      ? (slot.label || slot.key)
      : media.folder === 'SERVICO'
        ? 'Imagens de saída'
        : 'Outras mídias';

    if (!groups.has(title)) groups.set(title, { title, items: [] });
    groups.get(title)!.items.push(media);
  }

  return Array.from(groups.values());
};

const MediaGroupView: React.FC<{ group: MediaGroup }> = ({ group }) => (
  <div className="cp-media-group">
    <div className="cp-media-group-title">{group.title}</div>
    <div className="cp-media-grid">
      {group.items.map((media: any) => (
        <div key={media.id} className="cp-media-item">
          {media.type === 'VIDEO' ? (
            <video src={media.url} controls preload="metadata" />
          ) : (
            <a href={media.url} target="_blank" rel="noopener noreferrer">
              <img src={media.url} alt={group.title} loading="lazy" />
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Observações gerais da sessão (at-50) — rótulo conforme entrada/saída. */
const SessionObservationView: React.FC<{ label: string; observation?: string | null }> = ({ label, observation }) => {
  if (!observation || !observation.trim()) return null;
  return (
    <div className="cp-observation">
      <div className="cp-observation-title">{label}</div>
      <div className="cp-observation-text">{observation}</div>
    </div>
  );
};

const SessionItemsView: React.FC<{ items: { templates: any[]; answers: any[] } }> = ({ items }) => {
  const answersByKey = useMemo(
    () => new Map((items?.answers || []).map((a: any) => [a.templateKey, a])),
    [items]
  );

  const byCategory = useMemo(() => {
    const out = new Map<string, any[]>();
    for (const template of items?.templates || []) {
      if (!answersByKey.has(template.key)) continue;
      const label = template.categoryLabel || 'Itens';
      if (!out.has(label)) out.set(label, []);
      out.get(label)!.push(template);
    }
    return out;
  }, [items, answersByKey]);

  if (!items?.answers?.length) return null;

  return (
    <div>
      {Array.from(byCategory.entries()).map(([categoryLabel, templates]) => (
        <div key={categoryLabel} className="cp-items-category">
          <div className="cp-items-category-title">{categoryLabel}</div>
          {templates.map((template: any) => {
            const answer: any = answersByKey.get(template.key);
            return (
              <div key={template.key} className="cp-item-row-wrapper">
                <div className="cp-item-row">
                  <span className="cp-item-label">{template.label}</span>
                  <span className={`cp-item-status cp-item-status--${String(answer.status).toLowerCase()}`}>
                    {STATUS_LABELS[answer.status] || answer.status}
                  </span>
                </div>
                {answer.note && (
                  <div className="cp-item-note">
                    <strong>Obs:</strong> {answer.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export interface ChecklistSessoesPublicasProps {
  entrada: any | null;
  saida: any | null;
  /** Títulos das duas seções — a página do acompanhamento já tem um título "Checklist" acima. */
  tituloEntrada?: string;
  tituloSaida?: string;
}

const ChecklistSessoesPublicas: React.FC<ChecklistSessoesPublicasProps> = ({
  entrada,
  saida,
  tituloEntrada = 'Checklist de entrada',
  tituloSaida = 'Saída do veículo',
}) => (
  <>
    {entrada && (
      <section className="cp-session">
        <h2 className="cp-session-title">{tituloEntrada}</h2>
        {entrada.completedAt && (
          <div className="cp-session-date">
            Concluído em {new Date(entrada.completedAt).toLocaleString('pt-BR')}
          </div>
        )}
        <SessionItemsView items={entrada.items} />
        {groupSessionMedia(entrada).map((group) => (
          <MediaGroupView key={group.title} group={group} />
        ))}
        <SessionObservationView label="Observações de Entrada" observation={entrada.observation} />
      </section>
    )}

    {saida && (
      <section className="cp-session">
        <h2 className="cp-session-title">{tituloSaida}</h2>
        {saida.completedAt && (
          <div className="cp-session-date">
            Concluído em {new Date(saida.completedAt).toLocaleString('pt-BR')}
          </div>
        )}
        {groupSessionMedia(saida).map((group) => (
          <MediaGroupView key={group.title} group={group} />
        ))}
        <SessionObservationView label="Observações de Saída" observation={saida.observation} />
      </section>
    )}

    {!entrada && !saida && <div className="cp-info">Nenhum checklist disponível para este veículo.</div>}
  </>
);

export default ChecklistSessoesPublicas;
